import { devMode } from '@interhyp/api-values';
import { IdentityDto } from '@interhyp/platform-auth';
import { useClientTheme } from '@platform/theme';
import { useMemo } from 'react';

import { useFilteredRemotes } from './useFilteredRemotes';
import { COMPANY_ID_INTERHYP } from '../constants/companies';
import { remotes, modules } from '../constants/modules/base';
import { remotes as developerRemotes } from '../constants/modules/developer';
import {
  remotes as interhypCustomerRemotes,
  modules as interhypCustomerModules,
} from '../constants/modules/interhypCustomer';
import {
  remotes as interhypProfessionalRemotes,
  modules as interhypProfessionalModules,
} from '../constants/modules/interhypProfessional';
import {
  remotes as partnerCustomerRemotes,
  modules as partnerCustomerModules,
} from '../constants/modules/partnerCustomer';
import {
  remotes as partnerProfessionalRemotes,
  modules as partnerProfessionalModules,
} from '../constants/modules/partnerProfessional';
import { LocalModule } from '../interfaces/LocalModule';
import { Remote } from '../interfaces/Remote';

/*
This "mocks" the module config. In the future this will be completely be configured via config service
*/
export const useRemoteConfig = ({
  companyId,
  userType,
}: {
  companyId?: string;
  userType?: IdentityDto['userType'];
}): { remotes: Remote[]; modules: LocalModule[] } => {
  const theme = useClientTheme();

  const filtered = useFilteredRemotes();

  const [isDev] = devMode.useValue();

  return useMemo(() => {
    const safeCompanyId = companyId ?? theme.companyId.toString();
    const safeUserType = userType ?? 'CUSTOMER';

    const baseRemotes = [...(isDev ? developerRemotes : []), ...remotes];

    if (safeCompanyId === COMPANY_ID_INTERHYP) {
      if (safeUserType === 'PROFESSIONAL') {
        return {
          remotes: filtered([...baseRemotes, ...interhypProfessionalRemotes]),
          modules: [...modules, ...interhypProfessionalModules],
        };
      }
      return {
        remotes: filtered([...baseRemotes, ...interhypCustomerRemotes]),
        modules: [...modules, ...interhypCustomerModules],
      };
    }
    if (safeUserType === 'PROFESSIONAL') {
      return {
        remotes: filtered([...baseRemotes, ...partnerProfessionalRemotes]),
        modules: [...modules, ...partnerProfessionalModules],
      };
    }
    return {
      remotes: filtered([...baseRemotes, ...partnerCustomerRemotes]),
      modules: [...modules, ...partnerCustomerModules],
    };
  }, [companyId, filtered, isDev, theme.companyId, userType]);
};
